import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import RegisterInterestModal from "@/components/RegisterInterestModal";
import { STATUS, getStatusMeta } from "@/lib/status";

/**
 * Universal programme / project / experience card.
 *
 * Renders the same visual language everywhere, with the CTA driven
 * by the entry's status:
 *   - LIVE_NOW / BOOKING_OPEN → routes to the `href`
 *   - everything else → opens the RegisterInterestModal
 *
 * Required props: `title`, `status`, plus an `image` and `desc` for
 * the visual layer. `href` is required when status is live/booking-open.
 */
export default function ProgrammeCard({
  title,
  desc,
  image,
  status = STATUS.REGISTER_INTEREST,
  href,
  focal = "center",
  testId,
  eyebrow,
}) {
  const meta = getStatusMeta(status);
  const [modalOpen, setModalOpen] = useState(false);
  const isRoute = meta.ctaIntent === "primary" && href;

  const body = (
    <>
      <div className="relative aspect-[4/5] overflow-hidden bg-zinc-900">
        {image && (
          <div
            className="absolute inset-0 bg-cover transition-transform duration-[1500ms] group-hover:scale-110"
            style={{ backgroundImage: `url(${image})`, backgroundPosition: focal }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/5" />
        <div className="absolute top-4 left-4">
          <StatusBadge status={status} />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-6">
          {eyebrow && (
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold mb-2">
              {eyebrow}
            </p>
          )}
          <h3 className="font-display font-bold uppercase text-xl sm:text-2xl leading-tight">
            {title}
          </h3>
          {desc && (
            <p className="mt-3 text-sm text-zinc-300 leading-relaxed line-clamp-3">
              {desc}
            </p>
          )}
          <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold group-hover:gap-3 transition-all">
            {meta.ctaLabel}
            <ArrowRight className="h-3 w-3" />
          </div>
        </div>
      </div>
    </>
  );

  const className =
    "group relative block overflow-hidden border border-white/5 hover:border-amber-400/40 transition-colors";

  if (isRoute) {
    return (
      <Link to={href} data-testid={testId} className={className}>
        {body}
      </Link>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid={testId}
        className={`${className} text-left w-full`}
      >
        {body}
      </button>
      <RegisterInterestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        programme={title}
        status={status}
        cta={meta.ctaLabel}
      />
    </>
  );
}
